"use client";

import { useEffect, useRef, type CSSProperties } from "react";

type SignalDepth = "far" | "mid" | "near";
type SignalTone = "purple" | "indigo" | "neutral";

interface Signal {
  id: string;
  x: string;
  y: string;
  rotation: number;
  scale: number;
  opacity: number;
  depth: SignalDepth;
  tone: SignalTone;
}

interface HomeSignalFieldProps {
  className?: string;
}

const SIGNALS: Signal[] = [
  { id: "north-west", x: "4%", y: "12%", rotation: -8, scale: 0.68, opacity: 0.13, depth: "far", tone: "purple" },
  { id: "north-mid", x: "25%", y: "4%", rotation: 5, scale: 0.48, opacity: 0.1, depth: "mid", tone: "indigo" },
  { id: "north-east", x: "77%", y: "10%", rotation: 9, scale: 0.72, opacity: 0.13, depth: "near", tone: "purple" },
  { id: "west", x: "13%", y: "39%", rotation: 7, scale: 0.46, opacity: 0.09, depth: "mid", tone: "neutral" },
  { id: "center-left", x: "36%", y: "31%", rotation: -5, scale: 0.56, opacity: 0.11, depth: "near", tone: "purple" },
  { id: "center-right", x: "65%", y: "35%", rotation: 4, scale: 0.5, opacity: 0.1, depth: "far", tone: "indigo" },
  { id: "east", x: "91%", y: "42%", rotation: -7, scale: 0.44, opacity: 0.09, depth: "mid", tone: "neutral" },
  { id: "south-west", x: "7%", y: "70%", rotation: -4, scale: 0.52, opacity: 0.08, depth: "near", tone: "indigo" },
  { id: "south-mid", x: "48%", y: "67%", rotation: 6, scale: 0.42, opacity: 0.08, depth: "far", tone: "neutral" },
  { id: "south-east", x: "82%", y: "65%", rotation: -6, scale: 0.58, opacity: 0.09, depth: "near", tone: "purple" },
];

const DEPTH_MULTIPLIERS: Record<SignalDepth, number> = {
  far: 0.3,
  mid: 0.6,
  near: 1,
};

const TONE_CLASSES: Record<SignalTone, string> = {
  purple: "bg-[var(--purple)]",
  indigo: "bg-[var(--brand-indigo)]",
  neutral: "bg-[var(--fg-tertiary)]",
};

const ROWS = [
  { width: 40, left: 13, inset: 12 },
  { width: 52, left: 19, inset: 6 },
  { width: 64, left: 25, inset: 0 },
] as const;

const MASK_STYLE: CSSProperties = {
  maskImage: "linear-gradient(to bottom, black 0%, black 38%, rgba(0, 0, 0, 0.55) 60%, transparent 88%)",
  WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 38%, rgba(0, 0, 0, 0.55) 60%, transparent 88%)",
};

function offsetVariable(depth: SignalDepth, axis: "x" | "y") {
  return `var(--home-signal-${depth}-${axis}, 0px)`;
}

export function HomeSignalField({ className = "" }: HomeSignalFieldProps) {
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const coarsePointer = window.matchMedia("(pointer: coarse)");
    let animationFrame: number | null = null;
    let pointerX = window.innerWidth / 2;
    let pointerY = window.innerHeight / 2;
    let pointerListenerAttached = false;

    const writeOffsets = (x: number, y: number) => {
      (Object.keys(DEPTH_MULTIPLIERS) as SignalDepth[]).forEach((depth) => {
        const multiplier = DEPTH_MULTIPLIERS[depth];
        field.style.setProperty(`--home-signal-${depth}-x`, `${(x * multiplier).toFixed(2)}px`);
        field.style.setProperty(`--home-signal-${depth}-y`, `${(y * multiplier).toFixed(2)}px`);
      });
    };

    const resetOffsets = () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      writeOffsets(0, 0);
    };

    const commitPointerPosition = () => {
      animationFrame = null;
      const width = Math.max(window.innerWidth, 1);
      const height = Math.max(window.innerHeight, 1);
      const x = Math.max(-8, Math.min(8, (pointerX / width - 0.5) * 16));
      const y = Math.max(-8, Math.min(8, (pointerY / height - 0.5) * 16));
      writeOffsets(x, y);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;

      pointerX = event.clientX;
      pointerY = event.clientY;
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(commitPointerPosition);
      }
    };

    const attachPointerListener = () => {
      if (pointerListenerAttached) return;
      window.addEventListener("pointermove", handlePointerMove, { passive: true });
      pointerListenerAttached = true;
    };

    const detachPointerListener = () => {
      if (!pointerListenerAttached) return;
      window.removeEventListener("pointermove", handlePointerMove);
      pointerListenerAttached = false;
    };

    const syncMotionPreference = () => {
      if (reducedMotion.matches || coarsePointer.matches) {
        detachPointerListener();
        resetOffsets();
        return;
      }

      attachPointerListener();
    };

    reducedMotion.addEventListener("change", syncMotionPreference);
    coarsePointer.addEventListener("change", syncMotionPreference);
    window.addEventListener("blur", resetOffsets);
    syncMotionPreference();

    return () => {
      detachPointerListener();
      reducedMotion.removeEventListener("change", syncMotionPreference);
      coarsePointer.removeEventListener("change", syncMotionPreference);
      window.removeEventListener("blur", resetOffsets);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div
      ref={fieldRef}
      data-home-signal-field
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 -top-8 h-[320px] select-none overflow-hidden ${className}`}
      style={MASK_STYLE}
    >
      {SIGNALS.map((signal) => (
        <div
          key={signal.id}
          className="absolute flex origin-center flex-col gap-1"
          style={{
            left: signal.x,
            top: signal.y,
            opacity: signal.opacity,
            transform: `translate3d(${offsetVariable(signal.depth, "x")}, ${offsetVariable(signal.depth, "y")}, 0) rotate(${signal.rotation}deg) scale(${signal.scale})`,
          }}
        >
          {ROWS.map((row) => (
            <span
              key={row.width}
              className="flex h-1 overflow-hidden rounded-full"
              style={{ width: row.width, marginLeft: row.inset }}
            >
              <span className="h-full shrink-0 bg-[var(--fg)]" style={{ width: row.left }} />
              <span className={`h-full flex-1 ${TONE_CLASSES[signal.tone]}`} />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
